import * as React from 'react';
import { ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils/cn';

export interface MethodChoiceCardProps {
  icon: React.ReactNode;
  title: string;
  description: string;
  badge?: string;
  onClick: () => void;
  disabled?: boolean;
  className?: string;
}

export function MethodChoiceCard({
  icon,
  title,
  description,
  badge,
  onClick,
  disabled,
  className,
}: MethodChoiceCardProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={cn(
        'group w-full rounded-lg border border-input bg-background p-4 text-left',
        'transition-colors hover:border-primary hover:bg-accent/40',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
        'disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:border-input disabled:hover:bg-background',
        className
      )}
    >
      <div className="flex items-start gap-3">
        <div
          className={cn(
            'flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-md',
            'bg-primary/10 text-primary',
            'transition-colors group-hover:bg-primary/15'
          )}
          aria-hidden="true"
        >
          {icon}
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-medium text-foreground">{title}</span>
            {badge && (
              <span
                className={cn(
                  'inline-flex items-center rounded-full bg-primary/10 px-2 py-0.5',
                  'text-[10px] font-medium uppercase tracking-wide text-primary'
                )}
              >
                {badge}
              </span>
            )}
          </div>
          <p className="mt-1 text-xs text-muted-foreground">{description}</p>
        </div>

        <ChevronRight
          className="h-4 w-4 flex-shrink-0 text-muted-foreground transition-transform group-hover:translate-x-0.5"
          aria-hidden="true"
        />
      </div>
    </button>
  );
}
